/**
 * Entity workbench — Manage tab (ENTITY_WORKBENCH_PLAN.md W7).
 *
 * Every surface that CHANGES an entity lives here, and only here. These
 * cards were the /entities inline expander's payload until 2026-08-12; the
 * expander now confirms an entity and never edits it (R18), so this is the
 * one home for editorial overrides, the LinkedIn company tag, source
 * mappings, enrichment, commissions and deletion.
 *
 * Moved verbatim in behaviour — the sticky-name semantics, the URN
 * verification gate and the mapping CRUD are unchanged; only their address
 * moved.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  CheckCircle2, Loader2, Plus, Sparkles, Trash2, X, XCircle,
} from 'lucide-react';
import {
  createSourceMapping, deleteEntity, deleteSourceMapping, enrichEntity,
  getEntity, updateEntity,
} from '../../lib/api/entities.js';
import { getEntitySocial, updateEntitySocial } from '../../lib/api/social.js';
import { pageNameCorroboratesEntity } from '../../lib/linkedinCorroboration.js';
import { LinkedInUrnHelper } from '../LinkedInUrnHelper.js';
import type {
  EntityDetail, EntitySourceMapping, SourceMappingCreateRequest,
} from '../../lib/api/types.js';
import { CommissionsCard } from '../CommissionsCard.js';
import {
  applyBackfill, getBackfillPlan, type BackfillCandidate, type BackfillPlan,
} from '../../lib/api/entity-workbench.js';

export function ManageTab({ entityId }: { entityId: string }) {
  const detailQuery = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => getEntity(entityId),
  });

  if (detailQuery.isLoading) {
    return <div className="py-16 text-center text-sm text-grey-500">Loading…</div>;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="py-16 text-center text-sm text-red-600">
        Could not load this entity's management data.
      </div>
    );
  }

  const detail = detailQuery.data as EntityDetail;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <EnrichButton entityId={entityId} />
        <DeleteEntityButton entityId={entityId} ticker={detail.canonical_identifier} />
      </div>

      <EditorialOverridesCard entityId={entityId} detail={detail} />
      <SocialHandlesCard
        entityId={entityId}
        entityName={detail.display_name}
        ticker={detail.canonical_identifier}
      />
      <SourceMappingsCard entityId={entityId} detail={detail} />
      <MetadataCard detail={detail} />
      <CommissionsCard entityId={entityId} />
      <BackfillHistoryCard entityId={entityId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editorial overrides (FR Step D — Layer 3 of the entity-name model)
// ---------------------------------------------------------------------------
// Setting a non-empty editorial_preferred_name makes it sticky — Prism
// inference will not overwrite. Clearing it (empty field + save) resets
// sticky and re-allows auto-inference.

function EditorialOverridesCard({ entityId, detail }: { entityId: string; detail: EntityDetail }) {
  const queryClient = useQueryClient();
  const [preferredName, setPreferredName] = useState(detail.editorial_preferred_name ?? '');
  const [avoidanceNotes, setAvoidanceNotes] = useState(detail.editorial_avoidance_notes ?? '');

  useEffect(() => {
    setPreferredName(detail.editorial_preferred_name ?? '');
    setAvoidanceNotes(detail.editorial_avoidance_notes ?? '');
  }, [detail.editorial_preferred_name, detail.editorial_avoidance_notes]);

  const isSticky = !!detail.editorial_preferred_name_set_by_operator;
  const dirty =
    (preferredName ?? '') !== (detail.editorial_preferred_name ?? '') ||
    (avoidanceNotes ?? '') !== (detail.editorial_avoidance_notes ?? '');

  const saveMutation = useMutation({
    mutationFn: () =>
      updateEntity(entityId, {
        editorial_preferred_name: preferredName,
        editorial_avoidance_notes: avoidanceNotes,
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
      queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });

  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-grey-900">Editorial overrides</h2>
        <span
          className={
            'text-xs px-2 py-0.5 rounded-full ' +
            (isSticky ? 'bg-amber-50 text-amber-700' : 'bg-grey-100 text-grey-600')
          }
          title={
            isSticky
              ? 'Operator-curated — Prism inference will NOT overwrite this value.'
              : 'System-derivable — Prism inference (≥0.92 confidence) may set or refine this value.'
          }
        >
          {isSticky ? 'Sticky (operator)' : 'Auto-derivable'}
        </span>
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-grey-600 mb-1">
            Editorial preferred name
          </label>
          <input
            value={preferredName}
            onChange={(e) => setPreferredName(e.target.value)}
            placeholder={`e.g. "${detail.display_name}"`}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-grey-400 mt-1">
            Short form used in article body copy. Falls back to display name when blank.
            Clearing the field re-allows Prism auto-inference.
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-grey-600 mb-1">
            Editorial avoidance notes
          </label>
          <textarea
            value={avoidanceNotes}
            onChange={(e) => setAvoidanceNotes(e.target.value)}
            placeholder="e.g. legal review required before any coverage; embargo until X."
            rows={2}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          {saveMutation.isError && (
            <span className="text-xs text-red-600 mr-auto">Save failed — try again</span>
          )}
          {saveMutation.isSuccess && !dirty && (
            <span className="text-xs text-green-600 mr-auto">Saved</span>
          )}
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!dirty || saveMutation.isPending}
            className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// LinkedIn company tag (entity social handles)
// ---------------------------------------------------------------------------
// `verified` gates whether the publisher tags this company on its LinkedIn
// posts. The exact URN is REQUIRED — a wrong URN silently tags the wrong
// company (proven by the S1b render-check), so verification is an operator
// gate. The background resolver auto-fills the URN when it can; this card is
// the manual confirm / fix path.

const RESOLUTION_BADGE: Record<string, { cls: string; label: string }> = {
  resolved: { cls: 'bg-green-50 text-green-700', label: 'Resolved' },
  resolving: { cls: 'bg-blue-50 text-blue-700', label: 'Resolving' },
  pending: { cls: 'bg-grey-100 text-grey-600', label: 'Queued' },
  needs_review: { cls: 'bg-amber-50 text-amber-700', label: 'Needs review' },
  failed: { cls: 'bg-red-50 text-red-700', label: 'Failed' },
};

function SocialHandlesCard({
  entityId,
  entityName,
  ticker,
}: {
  entityId: string;
  entityName?: string | null;
  ticker?: string | null;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['entity-social', entityId],
    queryFn: () => getEntitySocial(entityId),
  });

  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [linkedinUrn, setLinkedinUrn] = useState('');
  const [linkedinPageName, setLinkedinPageName] = useState('');
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (data) {
      setLinkedinUrl(data.linkedin_url ?? '');
      setLinkedinUrn(data.linkedin_urn ?? '');
      setLinkedinPageName(data.linkedin_page_name ?? '');
      setVerified(data.verified);
    }
  }, [data?.linkedin_url, data?.linkedin_urn, data?.linkedin_page_name, data?.verified]);

  const urnTrimmed = linkedinUrn.trim();
  const pageNameTrimmed = linkedinPageName.trim();
  const urnValid = urnTrimmed === '' || /^urn:li:organization:\d+$/.test(urnTrimmed);
  // The publisher anchors this exact page name verbatim in post text; tagging
  // silently no-ops without it. So a company can't be marked Verified (i.e.
  // taggable) unless the page name is present.
  const pageNameMissingForVerified = verified && pageNameTrimmed === '';
  // Advisory soft-warning: does the page name plausibly match this entity?
  const nameMismatch = pageNameTrimmed !== '' &&
    !pageNameCorroboratesEntity(pageNameTrimmed, entityName, ticker);
  const dirty = !!data && (
    linkedinUrl.trim() !== (data.linkedin_url ?? '') ||
    urnTrimmed !== (data.linkedin_urn ?? '') ||
    pageNameTrimmed !== (data.linkedin_page_name ?? '') ||
    verified !== data.verified
  );

  const saveMutation = useMutation({
    mutationFn: () => updateEntitySocial(entityId, {
      linkedin_url: linkedinUrl.trim() || null,
      linkedin_urn: urnTrimmed || null,
      linkedin_page_name: pageNameTrimmed || null,
      verified,
      source: 'manual',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-social', entityId] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-review-queue'] });
    },
  });

  const badge = data?.linkedin_resolution_status
    ? RESOLUTION_BADGE[data.linkedin_resolution_status]
    : null;

  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-grey-900">LinkedIn company tag</h2>
        {badge && (
          <span className={'text-xs px-2 py-0.5 rounded-full ' + badge.cls}>{badge.label}</span>
        )}
      </div>
      {isLoading ? (
        <p className="text-xs text-grey-400">Loading…</p>
      ) : (
        <div className="space-y-3">
          <LinkedInUrnHelper />
          <div>
            <label className="block text-xs font-medium text-grey-600 mb-1">Company page URL</label>
            <input
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://www.linkedin.com/company/<slug>"
              className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-grey-600 mb-1">Organisation URN</label>
            <input
              value={linkedinUrn}
              onChange={(e) => setLinkedinUrn(e.target.value)}
              placeholder="urn:li:organization:1234567"
              className={
                'w-full border rounded-lg px-3 py-2 text-sm ' +
                (urnValid ? 'border-grey-200' : 'border-red-300')
              }
            />
            {!urnValid && (
              <p className="text-xs text-red-600 mt-1">Must be urn:li:organization:&lt;numeric id&gt;</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-grey-600 mb-1">Page name</label>
            <input
              value={linkedinPageName}
              onChange={(e) => setLinkedinPageName(e.target.value)}
              placeholder="Exact company name as shown on the LinkedIn page"
              className={
                'w-full border rounded-lg px-3 py-2 text-sm ' +
                (pageNameMissingForVerified ? 'border-red-300' : 'border-grey-200')
              }
            />
            {pageNameMissingForVerified ? (
              <p className="text-xs text-red-600 mt-1">
                Required to verify — tagging anchors this name verbatim in the post, so it can't be blank.
              </p>
            ) : (
              <p className="text-xs text-grey-400 mt-1">
                Copy the company's display name from its LinkedIn page header, exactly as shown.
              </p>
            )}
            {nameMismatch && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
                ⚠ This page name doesn't obviously match <b>{entityName}</b>
                {ticker ? ` (${ticker})` : ''}. Double-check you have the right company's page
                before verifying.
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Verified — tag this company on LinkedIn posts.
              <span className="block text-xs text-grey-400">
                A wrong URN silently tags the wrong company. Only verify a URN you've confirmed.
              </span>
            </span>
          </label>
          <div className="flex items-center justify-end gap-2">
            {saveMutation.isError && (
              <span className="text-xs text-red-600 mr-auto">Save failed — try again</span>
            )}
            {saveMutation.isSuccess && !dirty && (
              <span className="text-xs text-green-600 mr-auto">Saved</span>
            )}
            {data?.updated_at && !saveMutation.isSuccess && (
              <span className="text-xs text-grey-400 mr-auto">
                {data.source} · {format(new Date(data.updated_at), 'd MMM yyyy')}
              </span>
            )}
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending || !urnValid || pageNameMissingForVerified}
              className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Source mappings
// ---------------------------------------------------------------------------

function SourceMappingsCard({ entityId, detail }: { entityId: string; detail: EntityDetail }) {
  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-grey-900">Source mappings</h2>
        <AddSourceMappingButton entityId={entityId} />
      </div>
      {detail.source_mappings.length === 0 ? (
        <p className="text-xs text-grey-400">No source mappings configured</p>
      ) : (
        <div className="space-y-2">
          {[...detail.source_mappings]
            .sort((a, b) => a.priority - b.priority)
            .map(mapping => (
              <SourceMappingRow key={mapping.mapping_id} mapping={mapping} entityId={entityId} />
            ))}
        </div>
      )}
    </section>
  );
}

function SourceMappingRow({ mapping, entityId }: { mapping: EntitySourceMapping; entityId: string }) {
  const queryClient = useQueryClient();

  const deleteMut = useMutation({
    mutationFn: () => deleteSourceMapping(entityId, mapping.mapping_id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['entity', entityId] }),
  });

  return (
    <div className="flex items-center justify-between gap-3 py-2 px-3 bg-grey-50 rounded-lg flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xs font-mono font-semibold text-grey-600 bg-grey-200 px-1.5 py-0.5 rounded">
          P{mapping.priority}
        </span>
        <div className="min-w-0">
          <span className="text-sm font-medium text-grey-800">{mapping.source_name}</span>
          <span className="text-xs text-grey-500 ml-2">{mapping.source_identifier}</span>
        </div>
        {mapping.is_active
          ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
          : <XCircle className="h-3.5 w-3.5 text-grey-400" />
        }
      </div>
      <div className="flex items-center gap-3 text-xs text-grey-500">
        <span>{mapping.fetch_count} fetches</span>
        {mapping.last_fetch_at && (
          <span>Last: {format(new Date(mapping.last_fetch_at), 'dd MMM HH:mm')}</span>
        )}
        <button
          onClick={e => { e.stopPropagation(); deleteMut.mutate(); }}
          className="text-red-400 hover:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function AddSourceMappingButton({ entityId }: { entityId: string }) {
  const [open, setOpen] = useState(false);
  const [sourceName, setSourceName] = useState('market_index');
  const [sourceId, setSourceId] = useState('');
  const [priority, setPriority] = useState(10);
  const queryClient = useQueryClient();

  const createMut = useMutation({
    mutationFn: (data: SourceMappingCreateRequest) => createSourceMapping(entityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
      setOpen(false);
      setSourceId('');
    },
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
      >
        <Plus className="h-3 w-3" /> Add
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={sourceName}
        onChange={e => setSourceName(e.target.value)}
        className="text-xs border border-grey-200 rounded px-2 py-1"
      >
        {/* Live ingress adapters only (2026-10-06): firecrawl_search, lse_investegate,
            company_ir and thepick were retired (backend 92605dec8); asx_direct is inactive. */}
        {['market_index', 'nsx', 'cboe_au'].map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Source ID"
        value={sourceId}
        onChange={e => setSourceId(e.target.value)}
        className="text-xs border border-grey-200 rounded px-2 py-1 w-24"
      />
      <input
        type="number"
        placeholder="Priority"
        value={priority}
        onChange={e => setPriority(Number(e.target.value))}
        className="text-xs border border-grey-200 rounded px-2 py-1 w-16"
      />
      <button
        onClick={() => createMut.mutate({ source_name: sourceName, source_identifier: sourceId || 'batch', priority })}
        disabled={createMut.isPending}
        className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
      >
        {createMut.isPending ? '...' : 'Save'}
      </button>
      <button onClick={() => setOpen(false)} className="text-grey-400 hover:text-grey-600">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw metadata (read-only — the enrichment surface writes it)
// ---------------------------------------------------------------------------

function MetadataCard({ detail }: { detail: EntityDetail }) {
  const entries = Object.entries(detail.metadata || {});
  if (entries.length === 0) return null;
  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <h2 className="text-sm font-semibold text-grey-900 mb-3">
        Metadata <span className="font-normal text-grey-400">— written by enrichment</span>
      </h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="flex gap-2 min-w-0 text-xs">
            <dt className="shrink-0 w-40 text-grey-400">{key}</dt>
            <dd className="text-grey-700 min-w-0 break-words">{String(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

function EnrichButton({ entityId }: { entityId: string }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<Record<string, any> | null>(null);

  const enrichMut = useMutation({
    mutationFn: () => enrichEntity(entityId),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['entity-workbench-overview', entityId] });
      setTimeout(() => setResult(null), 5000);
    },
  });

  return (
    <div className="relative">
      <button
        onClick={() => enrichMut.mutate()}
        disabled={enrichMut.isPending}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50"
      >
        {enrichMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        Enrich
      </button>
      {result && (
        <div className="absolute left-0 top-full mt-1 bg-surface border border-grey-200 rounded-lg shadow-lg p-3 z-10 min-w-[200px]">
          {result.enriched_fields?.length > 0 ? (
            <div className="text-xs text-success-700">
              Discovered: {result.enriched_fields.join(', ')}
              <div className="text-grey-400 mt-0.5">via {result.sources_checked?.join(', ')}</div>
            </div>
          ) : (
            <div className="text-xs text-grey-500">No new data found</div>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteEntityButton({ entityId, ticker }: { entityId: string; ticker: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => deleteEntity(entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      navigate('/entities');
    },
  });

  if (confirming) {
    return (
      <span className="flex items-center gap-2 ml-auto">
        <span className="text-xs text-grey-600">Delete {ticker} and everything bound to it?</span>
        <button
          onClick={() => deleteMutation.mutate()}
          disabled={deleteMutation.isPending}
          className="px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="px-3 py-1.5 text-xs font-medium text-grey-600 rounded-lg hover:bg-grey-50"
        >
          Keep
        </button>
      </span>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 ml-auto"
    >
      <Trash2 className="h-3.5 w-3.5" /> Delete
    </button>
  );
}

// ---------------------------------------------------------------------------
// Backfill history — HISTORICAL_BACKFILL_PUBLISH_PLAN.md (operator 2026-09-10)
// ---------------------------------------------------------------------------
// After-the-fact articles for a coverage customer's / named prospect's hub.
// Dry run first (mandatory, ruling 4); apply publishes silently, dated to
// each filing's session day. In-app confirm — never a native confirm().

const OUTCOME_LABEL: Record<string, string> = {
  covered: 'covered',
  publish_draft: 'publish existing draft',
  generate: 'write',
  blocked: 'blocked',
};

function BackfillHistoryCard({ entityId }: { entityId: string }) {
  const queryClient = useQueryClient();
  const [months, setMonths] = useState(12);
  const [limit, setLimit] = useState(50);
  const [plan, setPlan] = useState<BackfillPlan | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BackfillPlan | null>(null);

  const planMutation = useMutation({
    mutationFn: () => getBackfillPlan(entityId, { months, limit }),
    onMutate: () => {
      setRefusal(null);
      setResult(null);
      setConfirming(false);
    },
    onSuccess: (data) => setPlan(data),
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      setPlan(null);
      setRefusal(
        err?.response?.status === 409
          ? 'Only coverage customers and named prospects can be backfilled.'
          : (typeof detail === 'string' ? detail : detail?.detail) || 'Dry run failed — try again',
      );
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => applyBackfill(entityId, { months, limit }),
    onSuccess: (data) => {
      setResult(data);
      setPlan(null);
      setConfirming(false);
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
      queryClient.invalidateQueries({ queryKey: ['entity-overview', entityId] });
    },
    onError: () => setConfirming(false),
  });

  const eligible = plan?.candidates.filter((c) => c.status === 'eligible') ?? [];
  const writes = plan?.summary.generate ?? 0;
  const drafts = plan?.summary.publish_draft ?? 0;

  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-grey-900">Backfill history</h2>
        {plan?.groups?.length ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-grey-100 text-grey-600">
            {plan.groups.join(', ')}
          </span>
        ) : null}
      </div>
      <p className="text-xs text-grey-500 mb-3">
        Writes the articles this entity's processed filings never got, dated to each filing's
        session day and published silently — no socials, no notification drafts, not counted as
        today's output. Coverage customers and named prospects only. Dry run first.
      </p>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-grey-600 mb-1">Months back</label>
          <input
            type="number"
            min={1}
            max={12}
            value={months}
            onChange={(e) => setMonths(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
            className="w-24 border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-grey-600 mb-1">Max filings</label>
          <input
            type="number"
            min={1}
            max={50}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            className="w-24 border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={() => planMutation.mutate()}
          disabled={planMutation.isPending}
          className="px-3 py-2 text-xs font-medium border border-grey-200 rounded-lg hover:bg-grey-50 disabled:opacity-50"
        >
          {planMutation.isPending ? 'Checking…' : 'Dry run'}
        </button>
      </div>

      {refusal && <p className="text-xs text-red-600 mt-3">{refusal}</p>}

      {plan && (
        <div className="mt-4">
          <div className="text-xs text-grey-600 mb-2">
            {plan.summary.filings} filings in the last {plan.months} months ·{' '}
            <span className="font-medium text-grey-900">{eligible.length} eligible</span> ·{' '}
            {writes} to write · {drafts} drafts to publish · {plan.summary.covered ?? 0} already covered
          </div>
          {plan.candidates.length === 0 ? (
            <p className="text-xs text-grey-400">No processed filings in the window.</p>
          ) : (
            <BackfillTable candidates={plan.candidates} />
          )}
          {eligible.length > 0 && (
            <div className="flex items-center justify-end gap-2 mt-3">
              {applyMutation.isError && (
                <span className="text-xs text-red-600 mr-auto">Apply failed — try again</span>
              )}
              {confirming ? (
                <>
                  <span className="text-xs text-grey-700 mr-auto">
                    Publish {writes + drafts} article{writes + drafts === 1 ? '' : 's'} across{' '}
                    {eligible.length} filing{eligible.length === 1 ? '' : 's'}, dated to their
                    filing days? This cannot be undone from here.
                  </span>
                  <button
                    onClick={() => setConfirming(false)}
                    className="px-3 py-1.5 text-xs font-medium border border-grey-200 rounded-lg hover:bg-grey-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => applyMutation.mutate()}
                    disabled={applyMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {applyMutation.isPending ? 'Applying…' : 'Confirm backfill'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirming(true)}
                  className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Apply to {eligible.length} filing{eligible.length === 1 ? '' : 's'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="mt-4 text-xs">
          <p className="text-green-700 font-medium">
            Backfill queued: {result.enqueued?.length ?? 0} job{(result.enqueued?.length ?? 0) === 1 ? '' : 's'}.
            Articles land on the sites as they are written; each carries its filing day.
          </p>
          {result.enqueued?.some((e) => e.error) && (
            <p className="text-red-600 mt-1">
              {result.enqueued.filter((e) => e.error).length} item(s) could not be queued
              (no destination configured) — see the filing's page.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function BackfillTable({ candidates }: { candidates: BackfillCandidate[] }) {
  return (
    <div className="overflow-x-auto border border-grey-100 rounded-lg">
      <table className="min-w-full text-xs">
        <thead className="bg-grey-50 text-grey-600">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Filing day</th>
            <th className="text-left px-3 py-2 font-medium">Filing</th>
            <th className="text-left px-3 py-2 font-medium">Sub.</th>
            <th className="text-left px-3 py-2 font-medium">Mastheads</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => (
            <tr key={c.announcement_id} className={c.status === 'eligible' ? '' : 'text-grey-400'}>
              <td className="px-3 py-2 whitespace-nowrap">{c.session_date ?? '—'}</td>
              <td className="px-3 py-2">
                <a
                  href={`/announcements/${c.announcement_id}`}
                  className="hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {c.title ?? c.announcement_id}
                </a>
              </td>
              <td className="px-3 py-2">{c.substance ?? '—'}</td>
              <td className="px-3 py-2">
                {c.status === 'excluded' && c.products.length === 0 ? (
                  <span>{c.reason?.replace(/_/g, ' ')}</span>
                ) : (
                  c.products.map((p) => (
                    <span
                      key={p.product_id}
                      className={
                        'inline-block mr-1 mb-1 px-2 py-0.5 rounded-full ' +
                        (p.outcome === 'generate'
                          ? 'bg-primary-50 text-primary-700'
                          : p.outcome === 'publish_draft'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-grey-100 text-grey-500')
                      }
                      title={p.article_status ? `existing article: ${p.article_status}` : undefined}
                    >
                      {p.target_name ?? p.product_name}: {OUTCOME_LABEL[p.outcome] ?? p.outcome}
                    </span>
                  ))
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
